import { Alert, Button, Badge, Image, Text, Card, CardColors, Icon } from '@/ui'
import {
  createUpriseAccount,
  FinancialAdvisoryFeatureFlagValues,
  FinancialAdvisoryProfile,
  FinancialAdvisoryProfilesStatus,
  putFinancialAdvisoryProfile,
} from '../financialAdvisory.slice'
import { useCallback, useEffect, useState } from 'react'
import { track } from '@amplitude/analytics-browser'
import { useAnalyticsView } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  StripeLineItem,
  fetchCheckoutSession,
} from '@/actions/settings/billingActions'
import { fetchAdvisoryPrices } from '@/constants/pricingConstants'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { UpriseIntroCard } from './IntroCallCTAPanel'
import {
  getAdvisoryCallPricing,
  getLatestActiveIntroUserFinancialAdvisoryCallEvent,
} from '../financialAdvisory.selectors'
import { useStringFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import {
  hasAdvisoryDiscount,
  userGets3MonthsFreeAdvisoryCouponId,
} from '../service'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { TypographyStyles } from '@/styles'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'

export const PostIntroCallPanel = ({
  faProfile,
}: {
  faProfile: FinancialAdvisoryProfile
}) => {
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const callPricing = useReselector(getAdvisoryCallPricing)
  const advisoryPrices = fetchAdvisoryPrices()
  const annualPlan = advisoryPrices.annual.priceId
  const monthlyPlan = advisoryPrices.monthly.priceId
  const user = useReselector(getCurrentUser)
  const introCallEvent = useReselector(
    getLatestActiveIntroUserFinancialAdvisoryCallEvent
  )
  const userHasAdvisoryDiscount = hasAdvisoryDiscount(faProfile, introCallEvent)
  const advisoryPilotEnabled =
    useStringFlagValue(
      FEATURE_FLAG_KEYS.financialAdvisoryPilot,
      FinancialAdvisoryFeatureFlagValues.noPilot
    ) === FinancialAdvisoryFeatureFlagValues.pilotEnabled
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, true)
  /**
   * Navs to Payment
   */
  const navToPayment = useCallback(
    async (selectedPriceId: string) => {
      if (!user?.id || !selectedPriceId) {
        return
      }
      track('click nav to advisory purchase — advisory page ', {
        fa_profile_id: faProfile.id,
        user_id: faProfile.userId,
      })

      const lineItems: StripeLineItem[] = [
        {
          priceId: selectedPriceId,
          quantity: 1,
        },
      ]
      const metadata: Record<string, string> = {}

      let subscriptionCheckoutUrl: string | null = null
      /**
       * If User is in Launch Group 3, we need to pass in a 3 months free coupon (ID: 3mo_free_advisory)
       * This is a Stripe coupon that is applied to the subscription if the User signs up within 7 days of the intro call
       *
       */
      const isAnnualPlan = selectedPriceId === annualPlan
      const couponId = userGets3MonthsFreeAdvisoryCouponId(
        faProfile,
        introCallEvent,
        isAnnualPlan
      )
      const res = await fetchCheckoutSession({
        userId: user?.id,
        lineItems,
        success_url: {
          url: '/advisory/overview?paymentComplete=true',
        },
        // Navigate back to current page with query params
        cancel_url: {
          url: `${location.pathname}${location.search}`,
        },
        freeTrialEnabled: undefined,
        freeTrialSearchParamValue: null,
        couponId: typeof couponId === 'string' ? couponId : null,
        metadata,
      })(dispatch)
      if (res) {
        subscriptionCheckoutUrl = res.url
      }

      if (subscriptionCheckoutUrl !== null) {
        window.location.href = subscriptionCheckoutUrl
      }
    },
    [user?.id, faProfile, annualPlan, introCallEvent, dispatch]
  )

  // This officially enables the user to start onboarding to Uprise
  const submitUpriseOnboarding = async () => {
    setLoading(true)
    setError('')
    try {
      track('started uprise onboarding — post cancellation or disinterest', {
        fa_profile_id: faProfile.id,
        user_id: faProfile.userId,
      })
      const response = await createUpriseAccount()(dispatch)
      await putFinancialAdvisoryProfile(faProfile.id, {
        status: FinancialAdvisoryProfilesStatus.onboarding,
      })(dispatch)
      if (!response) {
        setError('Something went wrong! Please refresh and try again.')
      }
    } catch {
      setError('An unexpected error occurred. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  // not_interested
  const declineUpriseOnboarding = async () => {
    track('declined uprise onboarding', {
      fa_profile_id: faProfile.id,
      user_id: faProfile.userId,
    })
    const updated = await putFinancialAdvisoryProfile(faProfile.id, {
      status: FinancialAdvisoryProfilesStatus.not_interested,
    })(dispatch)
    if (!updated) {
      setError('Something went wrong!')
    }
  }
  useEffect(() => {
    pageView('advisory: post intro call panel')
  }, [pageView])

  const renderButtonGrouping = () => {
    if (advisoryPilotEnabled) {
      return (
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="primary"
            loading={loading}
            onClick={() => submitUpriseOnboarding()}
          >
            Start Onboarding
          </Button>
          <Button
            variant="secondary"
            loading={loading}
            onClick={() => declineUpriseOnboarding()}
          >
            Decline Advisory
          </Button>
        </div>
      )
    } else {
      return (
        <div className="flex flex-col lg:flex-row gap-4">
          <Card color={CardColors.LEVEL_1} className="min-w-[300px] p-4">
            <Text variant={TypographyStyles.H3}>Annual Plan</Text>
            <div className="flex items-baseline gap-2 mt-2">
              <Text variant={TypographyStyles.BODY_MD} className="line-through">
                $29 /mo
              </Text>
              <Text variant={TypographyStyles.BODY_LG} className="text-brand">
                $25/mo (save 15%)
              </Text>
            </div>
            <Text
              variant={TypographyStyles.BODY_SM}
              className="text-darkGray mt-1"
            >
              Billed annually
            </Text>
            <Button
              className="mt-4"
              variant="primary"
              onClick={() => navToPayment(annualPlan)}
            >
              Select Plan
            </Button>
          </Card>

          <Card color={CardColors.LEVEL_1} className="min-w-[300px] p-4">
            <Text variant={TypographyStyles.H3}>Monthly Plan</Text>
            <Text
              variant={TypographyStyles.BODY_LG}
              className="text-brand mt-2"
            >
              $29 /mo
            </Text>
            <Text
              variant={TypographyStyles.BODY_SM}
              className="text-darkGray mt-1"
            >
              Billed monthly
            </Text>
            <Button
              className="mt-4"
              variant="secondary"
              onClick={() => navToPayment(monthlyPlan)}
            >
              Select Plan
            </Button>
          </Card>
        </div>
      )
    }
  }

  return (
    <>
      {error && (
        <Alert
          variant="error"
          description="It looks like we encountered an error while trying to onboard you! Please try again."
        />
      )}

      {userHasAdvisoryDiscount && (
        <div className="mb-6">
          <Card color={CardColors.PRIORITY}>
            <Text variant={TypographyStyles.DISPLAY_SM} className="text-brand">
              🎉 7 Day Special Offer 🎉
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              Get <b>3 Months free</b> if you sign up for Advisory Add-On within
              7 days of your Advisory Intro Call! Add financial advisory to your
              Heard experience to receive expert guidance from a Certified
              Financial Planner (CFP®) to support your financial goals.
            </Text>
            <Text
              variant={TypographyStyles.BODY_SM}
              className="text-darkGray mt-4"
            >
              Offer ends 1 week after your intro call
            </Text>
          </Card>
        </div>
      )}

      <Card color={CardColors.LEVEL_1}>
        <div className="flex flex-col lg:flex-row gap-8 px-4 py-6">
          <div className="w-full lg:w-2/3 space-y-4">
            <Badge color="green">One More Step</Badge>
            <Text variant={TypographyStyles.H1}>
              Introducing Advisory: Small business and personal financial
              planning for {replacementText} like you
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              We hoped you enjoyed your free introductory call with a Certified
              Financial Planner! To unlock expert financial guidance for your
              private practice, add the Advisory add on and receive your 100%
              personalized, comprehensive financial plan. With the help of your
              financial advisor, Heard is here to support you with your
              financial goals.
            </Text>
            {renderButtonGrouping()}
          </div>
          <Image
            className="w-full lg:w-1/3 flex justify-center items-center"
            src="https://heard-images.s3.amazonaws.com/assets/advisory_screen.svg"
            alt="heard financial advisory"
          />
        </div>
      </Card>

      <div className="my-8 flex flex-col lg:flex-row items-center gap-6 px-4">
        <Image
          className="w-full lg:w-1/2 flex justify-center"
          src="https://heard-images.s3.amazonaws.com/assets/advisory_goals.svg"
          alt="heard financial advisory"
        />
        <div className="w-full lg:w-1/2 space-y-4">
          <Text variant={TypographyStyles.H2}>
            Included in your Advisory Add On:
          </Text>
          <ul className="space-y-3 list-none">
            {[
              'A 100% comprehensive financial plan, build for both your personal and business financial goals',
              'Unlimited revisions of your financial plan, to accommodate changes in goals',
              'Expert guidance from your Advisor, who is a Certified Financial Planner (CFP®)',
              'Unlimited messaging with your advisor to keep your plan updated throughout the year',
              'Accountability in ensuring you are progressing towards your financial goals',
              `For additional support, one on one 45 minute follow up video calls at $${callPricing}`,
            ].map((text, idx) => (
              <li key={idx}>
                <Text variant={TypographyStyles.BODY_MD}>
                  <Icon
                    icon={faCheck}
                    color="text-forest"
                    className="mr-2 inline-block"
                  />
                  {text}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="my-8">
        <UpriseIntroCard />
      </div>

      {!advisoryPilotEnabled && (
        <Card color={CardColors.LEVEL_1}>
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6 px-4 py-6">
            <div className="text-center lg:text-left lg:w-1/3 space-y-2">
              <Text variant={TypographyStyles.H2}>
                Get Started with Financial Advisory
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                $29 per month, or $300 per year
              </Text>
            </div>
            <div className="w-full lg:w-1/3">
              <Button
                variant="primary"
                className="w-full"
                onClick={() => navToPayment(annualPlan)}
              >
                Get Started with Annual Plan (Save 15%)
              </Button>
            </div>
            <div className="w-full lg:w-1/3">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => navToPayment(monthlyPlan)}
              >
                Get Started with Monthly Plan
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="my-12" />
    </>
  )
}
